export class MsgFileCacheDirHandler {
  readonly dirHandlerPromise: Promise<FileSystemDirectoryHandle>;
  readonly metaDirHandlerPromise: Promise<FileSystemDirectoryHandle>;
  readonly fileDirHandlerPromise: Promise<FileSystemDirectoryHandle>;
  constructor() {
    this.dirHandlerPromise = navigator.storage.getDirectory().then((root) =>
      root.getDirectoryHandle('halo-chat-msg-files', {
        create: true,
      })
    );
    this.metaDirHandlerPromise = this.dirHandlerPromise.then((dirHandler) =>
      dirHandler.getDirectoryHandle('meta', {
        create: true,
      })
    );
    this.fileDirHandlerPromise = this.dirHandlerPromise.then((dirHandler) =>
      dirHandler.getDirectoryHandle('file', {
        create: true,
      })
    );
  }
}

export interface FileMeta {
  name: string;
  size: number;
  type: string;
  created: number;
  lastRead: number;
  lastModified: number;
  ext: string;
}

export class MsgFileCache {
  private readonly msgFileCacheDirHandler: MsgFileCacheDirHandler;
  constructor(
    readonly cli_msg_id: string,
    msgFileCacheDirHandler?: MsgFileCacheDirHandler
  ) {
    this.msgFileCacheDirHandler = msgFileCacheDirHandler || new MsgFileCacheDirHandler();
  }

  async setMeta(file: File) {
    const { msgFileCacheDirHandler } = this;
    const metaDirHandle = await msgFileCacheDirHandler.metaDirHandlerPromise;

    const metaHandle = await metaDirHandle.getFileHandle(this.cli_msg_id, {
      create: true,
    });

    const writable = await metaHandle.createWritable();

    const fileMeta: FileMeta = {
      name: file.name,
      size: file.size,
      type: file.type,
      created: Date.now(),
      lastRead: Date.now(),
      lastModified: file.lastModified,
      ext: file.name.split('.').pop() || '',
    };

    await writable.write(JSON.stringify(fileMeta));
    await writable.close();
  }

  async updateMeta(meta: FileMeta) {
    const { msgFileCacheDirHandler } = this;
    const metaDirHandle = await msgFileCacheDirHandler.metaDirHandlerPromise;

    const metaHandle = await metaDirHandle.getFileHandle(this.cli_msg_id);

    const writable = await metaHandle.createWritable();

    await writable.write(JSON.stringify(meta));
    await writable.close();
  }

  async getMeta() {
    const { msgFileCacheDirHandler } = this;
    const metaDirHandle = await msgFileCacheDirHandler.metaDirHandlerPromise;
    const metaHandle = await metaDirHandle.getFileHandle(this.cli_msg_id);
    const file = await metaHandle.getFile();
    return JSON.parse(await file.text()) as FileMeta;
  }

  async setFile(file: File) {
    const { msgFileCacheDirHandler, cli_msg_id } = this;
    const dirHandler = await msgFileCacheDirHandler.fileDirHandlerPromise;
    const fileHandle = await dirHandler.getFileHandle(cli_msg_id, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(file);
    await writable.close();
    await this.setMeta(file);
  }

  async getFile() {
    const { msgFileCacheDirHandler, cli_msg_id } = this;
    const dirHandler = await msgFileCacheDirHandler.fileDirHandlerPromise;
    try {
      const [meta, fileHandle] = await Promise.all([
        this.getMeta(),
        dirHandler.getFileHandle(cli_msg_id),
      ]);

      const data = await fileHandle.getFile();

      const file = new File([data], meta.name, {
        type: meta.type,
        lastModified: data.lastModified,
      });

      this.updateMeta({
        ...meta,
        lastRead: Date.now(),
      });

      return file;
    } catch (err) {
      if (err.name === 'NotFoundError') {
        return null;
      }

      throw err;
    }
  }

  deleteFile() {
    const { msgFileCacheDirHandler, cli_msg_id } = this;
    return Promise.allSettled([
      msgFileCacheDirHandler.metaDirHandlerPromise
        .then((metaDirHandle) => metaDirHandle.getFileHandle(this.cli_msg_id))
        .then((metaHandle) => {
          // @ts-expect-error FileSystemFileHandle.remove is not defined in the type definitions
          return metaHandle.remove();
        }),
      msgFileCacheDirHandler.fileDirHandlerPromise
        .then((fileDirHandle) => fileDirHandle.getFileHandle(cli_msg_id))
        .then((fileHandle) => {
          // @ts-expect-error FileSystemFileHandle.remove is not defined in the type definitions
          return fileHandle.remove();
        }),
    ]);
  }

  async existsFile() {
    const { msgFileCacheDirHandler, cli_msg_id } = this;
    return msgFileCacheDirHandler.metaDirHandlerPromise
      .then((metaDirHandle) => metaDirHandle.getFileHandle(cli_msg_id))
      .then(
        () => true,
        () => false
      );
  }
}

export class MsgFileCacheManager {
  private readonly msgFileCacheDirHandler = new MsgFileCacheDirHandler();
  async each(callback: (msgFileCache: MsgFileCache) => Promise<void>) {
    const dirHandler = await this.msgFileCacheDirHandler.metaDirHandlerPromise;
    for await (const name of (
      dirHandler as unknown as { keys: () => AsyncIterable<string> }
    ).keys()) {
      const msgFileCache = new MsgFileCache(name, this.msgFileCacheDirHandler);
      await callback(msgFileCache);
    }
  }
}
